'use client';

import { ChevronsUpDown, ExternalLink, LogOut, Users } from 'lucide-react';
import { DropdownMenu } from 'radix-ui';
import { type ReactNode, useState } from 'react';
import { Avatar } from './avatar';
import { cn, focusRing } from './cn';
import { type CrowAppId, signOutEverywhere, useEcosystemAccount } from './ecosystem';
import { MenuItem, menuContentClass } from './menu';

export interface AccountPerson {
  name: string;
  email?: string | null;
  image?: string | null;
}

export interface AccountOrganization {
  name: string;
  /** Their role, in words ("Administrateur"). */
  role?: string | null;
}

/**
 * The account menu every Crowlingo app shows (the mockup, brand/crow-ecosysteme.html): the person
 * and organization from Zitadel, "Mon compte Crowlingo", the app's own entries, and one sign-out
 * that ends the session of every app (`signOutEverywhere`), then the app's own (`onSignOut`).
 */
export function AccountMenu({
  current,
  person,
  organization,
  onSignOut,
  children,
  variant = 'avatar',
  side = 'bottom',
  align = 'end',
  className,
}: {
  current: CrowAppId;
  person: AccountPerson;
  organization?: AccountOrganization | null;
  /** Ends this app's session and leaves (to its sign-in page). */
  onSignOut: () => void | Promise<void>;
  /** The app's own entries (its settings, theme), between the account and the sign-out. */
  children?: ReactNode;
  /** `avatar`: the round button of a top bar; `row`: the person's row at a sidebar's foot. */
  variant?: 'avatar' | 'row';
  side?: 'top' | 'bottom';
  align?: 'start' | 'center' | 'end';
  className?: string;
}) {
  const account = useEcosystemAccount();
  const [leaving, setLeaving] = useState(false);
  const leave = async () => {
    setLeaving(true);
    try {
      await signOutEverywhere(current);
      await onSignOut();
    } finally {
      setLeaving(false);
    }
  };
  const label = `${person.name}, menu du compte`;
  const trigger =
    variant === 'row' ? (
      <button
        type="button"
        aria-label={label}
        className={cn(
          'flex w-full min-w-0 items-center gap-2.5 rounded-crow-control px-2 py-1.5 text-left hover:bg-crow-raised data-[state=open]:bg-crow-raised',
          focusRing,
          className,
        )}
      >
        <Avatar name={person.name} src={person.image} />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-crow-compact font-semibold text-crow-text">
            {person.name}
          </span>
          <span className="block truncate text-crow-label text-crow-text-3">
            {organization ? organization.name : (person.email ?? '')}
          </span>
        </span>
        <ChevronsUpDown className="size-4 shrink-0 text-crow-text-3" />
      </button>
    ) : (
      <button
        type="button"
        aria-label={label}
        className={cn(
          'shrink-0 rounded-full data-[state=open]:ring-2 data-[state=open]:ring-crow-accent-tint',
          focusRing,
          className,
        )}
      >
        <Avatar name={person.name} src={person.image} />
      </button>
    );
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>{trigger}</DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          side={side}
          align={align}
          sideOffset={6}
          className={cn(menuContentClass, 'w-72 p-1.5')}
        >
          <div className="flex items-center gap-3 px-1.5 pt-1.5 pb-3">
            <Avatar name={person.name} src={person.image} size={40} />
            <div className="min-w-0">
              <b className="block truncate text-crow-compact font-semibold">{person.name}</b>
              {person.email ? (
                <p className="truncate text-crow-label text-crow-text-3">{person.email}</p>
              ) : null}
            </div>
          </div>
          {organization ? (
            <DropdownMenu.Item
              disabled={!account?.organizationUrl}
              onSelect={() =>
                account?.organizationUrl &&
                window.open(account.organizationUrl, '_blank', 'noopener')
              }
              className="mx-0.5 mb-1 flex cursor-default items-center gap-2.5 rounded-crow-control border border-crow-line px-2.5 py-2 text-crow-compact outline-none data-[highlighted]:bg-crow-raised [&_svg]:size-4 [&_svg]:text-crow-text-2"
            >
              <Users />
              <span className="min-w-0 flex-1 truncate">{organization.name}</span>
              {organization.role ? (
                <span className="text-crow-label text-crow-text-3">{organization.role}</span>
              ) : null}
            </DropdownMenu.Item>
          ) : null}
          <DropdownMenu.Separator className="-mx-1.5 my-1 h-px bg-crow-line" />
          <MenuItem
            icon={<ExternalLink />}
            disabled={!account}
            onSelect={() => account && window.open(account.url, '_blank', 'noopener')}
          >
            Mon compte Crowlingo
          </MenuItem>
          {children}
          <DropdownMenu.Separator className="-mx-1.5 my-1 h-px bg-crow-line" />
          <MenuItem icon={<LogOut />} disabled={leaving} onSelect={() => void leave()}>
            Se déconnecter de toutes les apps
          </MenuItem>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
